import React, { useCallback, useContext, useMemo } from "react";
import {
	DragDropContext,
	Draggable,
	type DraggableChildrenFn,
	type DragStart,
	Droppable,
	type DropResult,
	type ResponderProvided,
} from "react-beautiful-dnd";

import { faFileSearch } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";

import ActionItemGroup from "~/actionItems/actionItemGroup/ActionItemGroup";
import { GROUP_BY_CUSTOM, GROUP_DROPPABLE_TYPE, Views } from "~/actionItems/constants";
import { ActionItemsContext } from "~/actionItems/newActionItems/ActionItemsContext";
import type { ActionItem, GroupType } from "~/actionItems/types";
import { ReactComponent as Illustration } from "~/legacy-assets/images/illustrations/empty-state.svg";

import styles from "./ActionItemGroupsRenderer.module.css";

function getDraggableInnerRenderer(
	groups: Array<GroupType>,
	adjacentItemSelect: (item: ActionItem) => void,
): DraggableChildrenFn {
	return function DraggableGroupClone(provided, snapshot, rubric) {
		const group = groups[rubric.source.index];
		return (
			<ActionItemGroup
				group={group}
				adjacentItemSelect={adjacentItemSelect}
				{...provided.draggableProps}
				{...provided.dragHandleProps}
				ref={provided.innerRef}
			/>
		);
	};
}

interface ActionItemGroupsRendererProps {
	groups: Array<GroupType>;
	onDropEnd(result: DropResult, provided: ResponderProvided): void;
}

export default function ActionItemGroupsRenderer({ groups, onDropEnd }: ActionItemGroupsRendererProps) {
	const {
		filters,
		itemBeingEdited,
		selectedActionItems,
		setSelectedActionItems,
		adjacentItemSelect,
		setDraggingId,
		view,
		inPanel,
	} = useContext(ActionItemsContext);
	const { groupBy, showCreatedByYou, search } = filters;

	const inRecording = view === Views.RECORDING;

	const isDropDisabled = useMemo(() => groupBy !== GROUP_BY_CUSTOM, [groupBy]);
	const onAdjacentItemSelected = useCallback(
		(item: ActionItem) => adjacentItemSelect?.(item, groups),
		[adjacentItemSelect, groups],
	);
	const draggableInnerRenderer = useMemo(
		() => getDraggableInnerRenderer(groups, onAdjacentItemSelected),
		[groups, onAdjacentItemSelected],
	);
	const onDragStart = useCallback(
		(event: DragStart) => {
			if (!selectedActionItems.find(selectedActionItem => selectedActionItem.id === event.draggableId)) {
				setSelectedActionItems?.([]);
			}
			setDraggingId?.(event.draggableId);
		},
		[setDraggingId, selectedActionItems, setSelectedActionItems],
	);
	const onDragDropEnd = useCallback(
		(result: DropResult, provided: ResponderProvided) => {
			setDraggingId?.(undefined);
			onDropEnd(result, provided);
		},
		[setDraggingId, onDropEnd],
	);
	const hasFiltersApplied = useMemo(() => showCreatedByYou || search !== "", [showCreatedByYou, search]);

	return (
		<>
			{groups.length > 0 ? (
				<DragDropContext onDragStart={onDragStart} onDragEnd={onDragDropEnd}>
					<Droppable
						type={GROUP_DROPPABLE_TYPE}
						droppableId="ACTION_ITEM_ROOT"
						renderClone={draggableInnerRenderer}
						isDropDisabled={isDropDisabled}
					>
						{provided => (
							<div
								{...provided.droppableProps}
								ref={provided.innerRef}
								className={cx(styles.groupsList, {
									[styles.groupsListInPanel]: inPanel,
									[styles.groupsListInRecording]: inRecording,
								})}
							>
								{groups.map((group, index) => (
									<Draggable
										key={group.id}
										draggableId={group.id}
										index={index}
										isDragDisabled={
											isDropDisabled || group.items.edges.some(edge => edge?.node.id === itemBeingEdited?.id)
										}
										disableInteractiveElementBlocking // because our main drag handle area is interactable
									>
										{draggableInnerRenderer}
									</Draggable>
								))}
								{provided.placeholder}
							</div>
						)}
					</Droppable>
				</DragDropContext>
			) : (
				<div className={styles.emptyState}>
					{hasFiltersApplied ? (
						<>
							<FontAwesomeIcon icon={faFileSearch} className={styles.searchIcon} />
							<p>No action items found. {search === "" ? "" : "Try different keywords."}</p>
						</>
					) : (
						<>
							<Illustration className={styles.illustration} />
							<p>
								{view === Views.TEAMMATES
									? "Your teammates' action items that are visible to you will be displayed here."
									: "Action items in the series will be displayed here."}
							</p>
						</>
					)}
				</div>
			)}
		</>
	);
}
